import { useCallback, useEffect, useState } from 'react';
import { Archive, ArchiveRestore, ChevronDown, ChevronUp, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  applyBuiltInOverrides, BUILT_IN_DEFAULT_LOOK, BUILT_IN_FLAG_TYPES, createFlagType, customToDef, deleteFlagType, fetchBuiltInOverrides,
  fetchFlagTypes, resetBuiltInOverride, saveBuiltInOverride, shortcutTypes, updateFlagType, type BuiltInOverride, type FlagTypeRow,
} from '../../lib/flags';
import { useFlagStore } from '../../contexts/FlagStateContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { FlagIcon } from './FlagIcon';
import { FlagTypeForm } from './FlagTypeForm';

/**
 * Settings > Flags (#316): the built-in types, whose name, icon and colour can
 * be changed and reset (#332), and the custom ones, which can be added,
 * edited, reordered, archived, or deleted while unused. Each type's editor
 * also holds its "When made into a task" settings (#337).
 */
export function FlagTypesSettings() {
  const store = useFlagStore();
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<FlagTypeRow[]>([]);
  const [overrides, setOverrides] = useState<BuiltInOverride[]>([]);
  const [editing, setEditing] = useState<string | 'new' | null>(null);

  const load = useCallback(() => {
    fetchFlagTypes().then(setRows).catch(() => toast.error('Could not load flag types.'));
    fetchBuiltInOverrides().then(setOverrides).catch(() => setOverrides([]));
  }, [toast]);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); store.invalidate(); };

  const run = async (work: () => Promise<unknown>, failed: string) => {
    try { await work(); changed(); } catch { toast.error(failed); }
  };

  const move = (i: number, by: -1 | 1) => {
    const a = rows[i];
    const b = rows[i + by];
    if (!a || !b) return;
    const [pa, pb] = a.position === b.position ? [i + 1 + by, i + 1] : [b.position, a.position];
    void run(async () => { await updateFlagType(a.id, { position: pa }); await updateFlagType(b.id, { position: pb }); }, 'Could not reorder.');
  };

  const remove = async (r: FlagTypeRow) => {
    const ok = await confirm({ title: `Delete "${r.name}"?`, message: 'Only a type no flag uses can be deleted.', confirmLabel: 'Delete', variant: 'danger' });
    if (ok !== true) return;
    try {
      await deleteFlagType(r.id);
      changed();
    } catch (err) {
      toast.error(String(err).includes('409') ? 'This type is in use - archive it instead.' : 'Could not delete the type.');
    }
  };

  const builtIns = applyBuiltInOverrides(BUILT_IN_FLAG_TYPES, overrides);
  const keys = new Map(shortcutTypes([...builtIns, ...rows.map(customToDef)]).map((t, i) => [t.id, i + 1]));
  const icon = 'grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-bg-hover hover:text-text-primary disabled:opacity-30';

  return (
    <div className="flex flex-col gap-1 py-1">
      {builtIns.map((t) => {
        const o = overrides.find((x) => x.key === t.id);
        const look = BUILT_IN_DEFAULT_LOOK[t.id];
        return editing === t.id ? (
          <FlagTypeForm key={t.id} builtIn submitLabel="Save" typeId={t.id} taskRules
            initial={{
              name: t.label, icon: o?.icon ?? look.icon, color: o?.color ?? look.color,
              task_priority: o?.task_priority ?? null, task_tag: o?.task_tag ?? null, task_follow_up: o?.task_follow_up ?? null,
            }}
            onSubmit={async (input) => {
              await saveBuiltInOverride(t.id, {
                name: input.name, icon: input.icon, color: input.color,
                task_priority: input.task_priority, task_tag: input.task_tag, task_follow_up: input.task_follow_up,
              });
              setEditing(null);
              changed();
            }}
            onCancel={() => setEditing(null)} />
        ) : (
          <div key={t.id} className="flex items-center gap-1 px-1 py-0.5 text-sm text-text-primary">
            <FlagIcon def={t} /><span className="ml-1 flex-1">{t.label}</span>
            <kbd className="font-mono text-[10px] text-text-muted border border-border-hover rounded px-1">Alt+{keys.get(t.id)}</kbd>
            <button type="button" className={icon} aria-label={`Edit ${t.label}`} onClick={() => setEditing(t.id)}><Pencil size={14} aria-hidden /></button>
            {o && (
              <button type="button" className={icon} aria-label={`Reset ${t.label}`} title="Back to the default name, icon, colour and task settings"
                onClick={() => void run(() => resetBuiltInOverride(t.id), 'Could not reset the type.')}>
                <RotateCcw size={14} aria-hidden />
              </button>
            )}
          </div>
        );
      })}
      {rows.map((r, i) => (editing === r.id ? (
        <FlagTypeForm key={r.id} initial={r} submitLabel="Save" typeId={r.id} taskRules
          onSubmit={async (input) => { await updateFlagType(r.id, input); setEditing(null); changed(); }}
          onCancel={() => setEditing(null)} />
      ) : (
        <div key={r.id} className="flex items-center gap-1 px-1 py-0.5 text-sm text-text-primary">
          <FlagIcon def={customToDef(r)} />
          <span className="ml-1 flex-1 truncate">{r.name}</span>
          {r.archived_at
            ? <span className="rounded-full border border-border px-2 text-xs text-text-muted">Archived</span>
            : keys.has(r.id) && <kbd className="font-mono text-[10px] text-text-muted border border-border-hover rounded px-1">Alt+{keys.get(r.id)}</kbd>}
          <button type="button" className={icon} aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => move(i, -1)}><ChevronUp size={15} aria-hidden /></button>
          <button type="button" className={icon} aria-label={`Move ${r.name} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}><ChevronDown size={15} aria-hidden /></button>
          <button type="button" className={icon} aria-label={`Edit ${r.name}`} onClick={() => setEditing(r.id)}><Pencil size={14} aria-hidden /></button>
          <button type="button" className={icon} aria-label={`${r.archived_at ? 'Unarchive' : 'Archive'} ${r.name}`}
            onClick={() => void run(() => updateFlagType(r.id, { archived: !r.archived_at }), 'Could not change the type.')}>
            {r.archived_at ? <ArchiveRestore size={14} aria-hidden /> : <Archive size={14} aria-hidden />}
          </button>
          <button type="button" className={`${icon} hover:text-danger`} aria-label={`Delete ${r.name}`} onClick={() => void remove(r)}><Trash2 size={14} aria-hidden /></button>
        </div>
      )))}
      {editing === 'new' ? (
        <FlagTypeForm taskRules onSubmit={async (input) => { await createFlagType(input); setEditing(null); changed(); }} onCancel={() => setEditing(null)} />
      ) : (
        <button type="button" onClick={() => setEditing('new')} className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary">
          <Plus size={14} aria-hidden />New type
        </button>
      )}
    </div>
  );
}
